<script setup lang="ts">
import localforage from "localforage";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { applicationRouteMap } from "~/router/mainRouter";
import logo from "~/YYC_Housing/Intake/assets/YYCSeniorsIntakeLOGO.png";
import HelpSupportButton from "~/components/HelpSupportButton.vue";
import NotificationBell from "~/components/NotificationBell.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { authStore } from "~/stores/authStore";
import { apiClient } from "~/utils/apiClient";
import { commitBuildInfo, refreshCaches } from "~/utils/miscUtils";

const router = useRouter();
const myInfoStore = useMyInfoStore();

/** Routes to display on the page header */
const isLoggingOut = ref(false);
const logout = async () => {
  if (isLoggingOut.value) {
    return;
  }
  isLoggingOut.value = true;

  const token = await authStore.getAccessTokenAsync();

  if (token) {
    await apiClient.userInfo.forgetAboutMe();
  } else {
    console.warn("No Token");
  }

  await authStore.logout().then(() => {
    localforage.clear();
    isLoggingOut.value = false;
    router.push("/");
  });
};

const localRefresh = async () => {
  await refreshCaches();
  router.push("/");
};

onMounted(async () => {
  await myInfoStore.load();
});

const nameInfoVisible = ref(false);
</script>

<template>
  <div class="portalTopBar">
    <div class="title">
      <img
        :src="logo"
        alt="YYC Seniors Housing"
        class="logo"
        :title="commitBuildInfo"
      />
    </div>
    <h1>HAVN Portal <span class="v2-badge">v2</span></h1>
    <div>
      <div class="loginButtons">
        <HelpSupportButton
          v-if="myInfoStore.isLoggedIn"
          :to="`${applicationRouteMap.HMB.path}/support-tickets`"
        />
        <NotificationBell v-if="myInfoStore.isLoggedIn" />
        <el-popover :visible="nameInfoVisible" placement="bottom" :width="800">
          <el-descriptions border>
            <el-descriptions-item label="User" :span="2">
              <el-text class="nowrap">
                {{ myInfoStore.userInfo?.loginName }}
              </el-text>
            </el-descriptions-item>
            <el-descriptions-item label="Email" :span="2">
              <el-text class="nowrap">
                {{ myInfoStore.userInfo?.email }}
              </el-text>
            </el-descriptions-item>
            <el-descriptions-item label="ID" :span="2">
              <el-text class="nowrap">
                {{ myInfoStore.userInfo?.userGuid }}
              </el-text>
            </el-descriptions-item>

            <el-descriptions-item label="Organization" :span="2">
              <el-tag>
                {{ myInfoStore.userInfo?.defaultSysOrgGuid ?? "None" }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
          <div class="userInfoButtons">
            <el-button size="small" @click="localRefresh"
              >Refresh App Info</el-button
            >
            <el-button
              size="small"
              type="primary"
              @click="nameInfoVisible = false"
              >Close</el-button
            >
          </div>
          <template #reference>
            <span class="userInfoDisplay" @click="nameInfoVisible = true">
              <span v-if="myInfoStore.userInfo" :title="'Click for details'">
                {{ myInfoStore.userInfo?.loginName }}
              </span>
              <span v-else> Loading... </span>
            </span>
          </template>
        </el-popover>

        <el-link :disabled="isLoggingOut" @click="logout"> Logout </el-link>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.portalTopBar {
  height: var(--el-header-height);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title {
    display: flex;
    align-items: center;
    gap: 2em;
  }
  h1 {
    font-weight: 500;
    color: black;
    display: flex;
    align-items: center;
    gap: 0.5em;

    .v2-badge {
      font-size: 0.45em;
      font-weight: 700;
      background: #409eff;
      color: white;
      padding: 0.15em 0.5em;
      border-radius: 4px;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      vertical-align: middle;
    }
  }

  .logo {
    height: calc(var(--el-header-height) * 0.8);
    padding-top: 2px;
  }

  a.el-button {
    text-decoration: none;
  }

  .loginButtons {
    display: flex;
    align-items: center;
    gap: 2em;
  }

  // The portal top bar has a coloured background, so the default dark bell and
  // help icons are hard to see — force them white so they read clearly against
  // the blue bar, with a subtle hover. This block is intentionally NOT scoped,
  // so plain descendant selectors already reach the icons inside the child
  // components (no :deep() needed / allowed).
  .notification-bell .notification-bell-icon,
  .help-support .help-support-icon {
    color: white;

    &:hover {
      color: rgba(255, 255, 255, 0.85);
    }
  }

  // Keyboard focus affordance for the Help & Support link (it is a router-link).
  .help-support:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
    border-radius: 4px;
  }
}
.userInfoButtons {
  margin-top: 1em;
}
.usersRoles {
  display: flex;
  gap: 0.5em;
  .nowrap {
    white-space: nowrap;
  }
}
</style>
